import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import { iconNames } from "@/components/icons";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { KiiImageThumbnail } from "@/components/kii/kii-image-thumbnail";
import { KiiImageUploadZone } from "@/components/kii/kii-image-upload-zone";
import type { NMPhoto } from "@/components/kii/kii-editor";
import type { FileUploadProgress } from "@/hooks/useFileUpload";

export interface KiiImagePickerProps {
  images: NMPhoto[];
  onSelect: (photo: NMPhoto) => void;
  onUpload: (files: File[]) => void;
  onDelete?: (photoId: string) => Promise<void>;
  uploadProgress: FileUploadProgress[];
  isUploading: boolean;
  /** Preserve original aspect ratio for thumbnails instead of square crop */
  preserveAspectRatio?: boolean;
}

export function KiiImagePicker({
  images,
  onSelect,
  onUpload,
  onDelete,
  uploadProgress,
  isUploading,
  preserveAspectRatio,
}: KiiImagePickerProps) {
  const { t } = useTranslation();
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirmDelete = async () => {
    if (!deleteConfirmId || !onDelete) return;
    setIsDeleting(true);
    try {
      await onDelete(deleteConfirmId);
    } catch (err) {
      console.error("Failed to delete image:", err);
    } finally {
      setIsDeleting(false);
      setDeleteConfirmId(null);
    }
  };

  return (
    <div className="flex flex-col gap-4 h-full">
      {/* Image Grid */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {images.length > 0 ? (
          <div className={preserveAspectRatio ? "flex flex-col gap-2" : "grid grid-cols-2 gap-2"}>
            {images.map((photo) => (
              <KiiImageThumbnail
                key={photo.id}
                photo={photo}
                onClick={() => onSelect(photo)}
                preserveAspectRatio={preserveAspectRatio}
                actions={
                  onDelete ? (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirmId(photo.id);
                      }}
                      className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity absolute top-1 right-1"
                    >
                      <Icon name={iconNames.delete} size={14} />
                    </Button>
                  ) : undefined
                }
              />
            ))}
          </div>
        ) : (
          !isUploading && (
            <div className="text-center py-8 text-muted-foreground text-sm border border-dashed border-border rounded-lg">
              <Icon name={iconNames.image} size={32} className="mx-auto mb-2 opacity-40" />
              <p>{t("imagePicker.noImages", "No images yet")}</p>
              <p className="text-xs mt-1">
                {t("imagePicker.uploadHint", "Upload images below to get started.")}
              </p>
            </div>
          )
        )}
      </div>

      {/* Upload Zone */}
      <div className="shrink-0">
        <KiiImageUploadZone
          onUpload={onUpload}
          uploadProgress={uploadProgress}
          isUploading={isUploading}
        />
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("imagePicker.deleteTitle", "Delete Image")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "imagePicker.deleteDescription",
                "This will permanently delete this image from the document. This action cannot be undone.",
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {t("common.cancel", "Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground"
            >
              {isDeleting
                ? t("common.deleting", "Deleting...")
                : t("common.delete", "Delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
